import React, { useContext, useEffect, useState } from "react";
import { Carousel, Col, Card, Row, Container } from "react-bootstrap";
import { FaMapMarkerAlt, FaBed, FaRulerCombined, FaBath } from "react-icons/fa";
import { InfoContext } from "../providers/InfosContextProvider";
import { useNavigate } from "react-router-dom";
import { buildPublicAssetPath } from "../utils/publicAssetPath";
import ProgressiveImage from "./ProgressiveImage";
import {
  IMAGE_SIZES,
  getPlaceholderStyle,
  preloadPropertyCover,
  useCarouselImages,
} from "../utils/images";

const FeaturedProperties = () => {
  const { houses, texts } = useContext(InfoContext);
  const featuredProperties = houses.filter((property) => property.featured);
  const navigate = useNavigate();
  const propertyRefPrefix = texts?.Property?.ref || "Ref: ";

  const [columns, setColumns] = useState(3);

  const handleCardClick = (id) => {
    navigate(`/property/${id}`);
  };

  const chunkArray = (array, size) => {
    const result = [];
    if (array.length <= size) {
      result.push(array);
      return result;
    }
    for (let i = 0; i < array.length; i += size) {
      let chunk = array.slice(i, i + size);
      if (chunk.length < size) {
        chunk = [...chunk, ...array.slice(0, size - chunk.length)];
      }
      result.push(chunk);
    }
    return result;
  };

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setColumns(1);
      } else if (width < 992) {
        setColumns(2);
      } else {
        setColumns(3);
      }
    };

    window.addEventListener("resize", updateColumns);
    updateColumns();

    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  const propertiesChunks = chunkArray(featuredProperties, columns);
  // The next group of cards is downloaded while the current one is on screen,
  // so the autoplay never slides in an empty card.
  const chunkCarousel = useCarouselImages(propertiesChunks.length, {
    preloadPrevious: false,
  });

  if (!featuredProperties.length) {
    return null;
  }

  return (
    <div className="featured-properties">
      <Row>
        <div className="title">
          <h1 className="title-small">{`${texts.featured.titleSmall}`}</h1>
          <h1 className="title-large">{`${texts.featured.titleLarge}`}</h1>
          <div className="title-bar"></div>
        </div>
      </Row>

      <Container className="p-3">
        <Carousel
          interval={5000}
          activeIndex={chunkCarousel.activeIndex}
          onSelect={chunkCarousel.onSelect}
          className="custom-carousel"
          controls={featuredProperties.length > columns}
          indicators={featuredProperties.length > columns}
        >
          {propertiesChunks.map((chunk, index) => (
            <Carousel.Item key={index}>
              <Row className="mt-15">
                {chunk.map((property) => (
                  <Col
                    key={`${index}-${property.id}`}
                    xs={12}
                    md={6}
                    lg={4}
                    className="mb-4"
                  >
                    <Card
                      className="property-card"
                      onClick={() => handleCardClick(property.id)}
                      onMouseEnter={() => preloadPropertyCover(property)}
                      onTouchStart={() => preloadPropertyCover(property)}
                    >
                      <Carousel
                        interval={null}
                        controls={false}
                        indicators={false}
                        pause={false}
                      >
                        {property.images.length > 0 ? (
                          // Only the cover is ever visible in this card.
                          property.images.slice(0, 1).map((image) => (
                            <Carousel.Item key={image}>
                              <div
                                className="property-image-container"
                                style={getPlaceholderStyle(image)}
                              >
                                {chunkCarousel.shouldRender(index) && (
                                  <ProgressiveImage
                                    src={image}
                                    sizes={IMAGE_SIZES.card}
                                    alt={`Casa ${property.id}`}
                                    onLoad={chunkCarousel.markReady}
                                    {...(index !==
                                      chunkCarousel.activeIndex && {
                                      loading: "eager",
                                      fetchPriority: "low",
                                    })}
                                  />
                                )}
                                <div className="property-price">
                                  {property.price}
                                </div>
                                <span className="ref-tag property-card-ref">
                                  {`${propertyRefPrefix}`} {property.id}
                                </span>
                              </div>
                            </Carousel.Item>
                          ))
                        ) : (
                          <Carousel.Item>
                            <div className="property-image-container">
                              <img
                                src={
                                  buildPublicAssetPath(
                                    "/assets/images/casas-do-nabao-logo.png",
                                  ) ||
                                  buildPublicAssetPath(
                                    "/assets/images/casas-do-nabao-logo.png",
                                  )
                                }
                                alt={`Casa ${property.id}`}
                                style={{
                                  objectFit: "contain",
                                  padding: "40px",
                                  backgroundColor: "#f8f8f8",
                                }}
                              />
                              <div className="property-price">
                                {property.price}
                              </div>
                              <span className="ref-tag property-card-ref">
                                {`${propertyRefPrefix}`} {property.id}
                              </span>
                            </div>
                          </Carousel.Item>
                        )}

                        <div className="properties-container">
                          {property.efficiency.length === 1 ? (
                            <div className="property-efficiency">
                              <img
                                src={buildPublicAssetPath(
                                  `/assets/images/efficiency/${property.efficiency[0]}_RGB.png`,
                                )}
                                alt={`Eficiência Energética ${property.efficiency[0]}`}
                              />
                            </div>
                          ) : (
                            property.efficiency.map((efficiency, index) => (
                              <Col
                                key={index}
                                xs={6}
                                className="property-efficiency"
                              >
                                <img
                                  src={buildPublicAssetPath(
                                    `/assets/images/efficiency/${efficiency}_RGB.png`,
                                  )}
                                  alt={`Eficiência Energética ${efficiency}`}
                                />
                              </Col>
                            ))
                          )}
                        </div>
                      </Carousel>

                      <div className="property-info">
                        <Row>
                          <Col xs={12}>
                            <span>
                              <FaMapMarkerAlt /> {property.location}
                            </span>
                          </Col>
                        </Row>
                        <Row className="justify-content-center align-items-end">
                          {property.area && (
                            <Col xs="auto">
                              <span>
                                <FaRulerCombined /> {property.area}
                              </span>
                            </Col>
                          )}
                          {property.bedrooms && (
                            <Col xs="auto">
                              <span>
                                <FaBed /> {property.bedrooms}
                              </span>
                            </Col>
                          )}
                          {property.bathrooms && (
                            <Col xs="auto">
                              <span>
                                <FaBath /> {property.bathrooms}
                              </span>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Carousel.Item>
          ))}
        </Carousel>
      </Container>
    </div>
  );
};

export default FeaturedProperties;
